import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import StatsCard from '../../components/ui/StatsCard';
import Badge from '../../components/ui/Badge';
import useTranslation from '../../hooks/useTranslation';
import { projectService } from '../../services/projectService';
import {
  getProjectQuittancesData,
  createDenonciation,
  updateDenonciation,
  deleteDenonciation,
  getLettreSignedUrl,
  validateLettreFile,
  addDocument,
  removeDocument,
  createQuittance,
  deleteQuittance,
  getDenonciationDeleteBlockers,
  deriveTypeDenonciation,
  uploadQuittanceSignee,
  removeQuittanceSignee,
  getQuittanceSigneeUrl,
  NO_DP_COLUMN_ID,
  dpCellState,
} from '../../services/denonciationsQuittancesService';
import SearchableSelect from '../../components/ui/SearchableSelect';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import PortalDropdown from '../../components/ui/PortalDropdown';
import DocumentAttach, { DocumentCountButton } from '../../components/ui/DocumentAttach';
import { supabase } from '../../lib/supabase';
import { formatCurrency } from '../../utils/formatCurrency';
import organizationService from '../../services/organizationService';

// ─── Helpers ─────────────────────────────────────────────────
const fmt = (val) => formatCurrency(val);

/**
 * Montant et sa mention sur UNE SEULE ligne.
 *
 * Passe dans la prop `value` de StatsCard, jamais dans `sublabel` : celle-ci
 * rend une seconde ligne, ce qui rendait ces deux tuiles plus hautes que les
 * trois tuiles de comptage. StatsCard est monte par 12 pages — on ne touche
 * pas au composant partage, on lui donne simplement du JSX la ou il en
 * accepte deja.
 */
const ValueWithNote = ({ amount, note }) => (
  <>
    {amount}
    {note ? (
      <span className="ml-1.5 text-[9px] font-normal text-gray-400">{note}</span>
    ) : null}
  </>
);

const fmtDate = (d, language) => {
  if (!d) return '—';
  return new Date(`${d}T00:00:00`)?.toLocaleDateString(
    language === 'fr' ? 'fr-CA' : 'en-CA',
    { year: 'numeric', month: 'short', day: 'numeric' }
  );
};

/**
 * Seuil UNIQUE de "solde nul", partage par l'affichage et par l'autorisation
 * d'emettre. Deux seuils pour la meme question finiraient par produire une
 * ligne grise avec un bouton actif.
 * 0,05 $ absorbe le bruit d'arrondi TTC sans jamais masquer un vrai solde.
 */
const SETTLED_TOLERANCE = 0.05;
const isSettled = (balance) =>
  balance !== null && Math.abs(balance) < SETTLED_TOLERANCE;

/**
 * Une finale ATTESTE que tout est regle. Si le solde n'est pas nul, elle ment :
 * le statut le dit au lieu de l'afficher en vert.
 *   finale + solde nul     -> "Regle — finale emise"        (vert)
 *   finale + solde non nul -> "Finale emise — solde non nul" (ambre)
 * Les lignes deja en base peuvent violer les regles A et B : le statut reste
 * juste malgre elles, sans rien reecrire.
 */
const rowStatus = (row, hasFinale) => {
  // Montant de contrat inconnu (bon de commande, regie) : AUCUN calcul de
  // solde ne peut prouver la cloture. La seule preuve valable est alors une
  // quittance FINALE signee. Sans elle, la ligne reste ouverte — elle ne
  // retombe jamais en "regle" par accident.
  const balanceUnknown = row?.balance == null;

  if (hasFinale) {
    if (balanceUnknown) return 'settledFinale';
    return isSettled(row?.balance) ? 'settledFinale' : 'finaleUnbalanced';
  }
  if (!row?.denonciation) return 'toNotify';
  if (!balanceUnknown && isSettled(row?.balance)) return 'settled';
  return 'partial';
};

const STATUS_VARIANT = {
  toNotify: 'grey',
  partial: 'amber',
  settled: 'green',
  settledFinale: 'green',
  finaleUnbalanced: 'amber',
};

// Nom de fichier lisible a partir d'un chemin de stockage.
const baseName = (path) => (path || '')?.split('/')?.pop() || '';

// Valeur du selecteur de DP pour "Sans DP" : chaine vide cote formulaire,
// null en base (payment_request_id nullable).
const NO_DP_VALUE = '';


const COVERAGE_VARIANT = { none: 'red', partial: 'amber', full: 'green' };

// Libelle i18n du modele tarifaire d'un contrat.
const PRICING_LABEL_KEY = {
  fixed: 'quittances.toolbar.pricingFixed',
  material_supply: 'quittances.toolbar.pricingMaterialSupply',
  labor_material: 'quittances.toolbar.pricingLaborMaterial',
};

const todayIso = () => new Date()?.toISOString()?.slice(0, 10);

// ─── Indicateur de lettre jointe (affichage seul) ────────────
const LetterIcon = ({ count = 0, title }) => {
  if (!count) return null;
  return (
    <Icon
      name="FileText"
      size={12}
      className="text-primary shrink-0 inline-block ml-1.5 align-middle"
      title={title}
    />
  );
};

// ─── Detail au survol d'une cellule "Quittance DPn" ──────────
// Factures de CETTE DP, puis une ligne pour le cumul des DP anterieures,
// puis le total (= montant de la cellule). Portail : le tableau defile et
// couperait une infobulle positionnee en absolu.
const DpCellDetails = ({ className, label, details, prior, priorRange, total, children }) => {
  const { t } = useTranslation();
  const anchorRef = useRef(null);
  const timerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const close = useCallback(() => {
    clearTimeout(timerRef?.current);
    setOpen(false);
  }, []);
  useEffect(() => () => clearTimeout(timerRef?.current), []);

  return (
    <span
      ref={anchorRef}
      className={className}
      onMouseEnter={() => {
        clearTimeout(timerRef?.current);
        timerRef.current = setTimeout(() => setOpen(true), 150);
      }}
      onMouseLeave={close}
    >
      {children}
      <PortalDropdown anchorRef={anchorRef} open={open} onClose={close} width={260}>
        <div className="px-3 py-1.5 text-xs font-normal text-gray-700 whitespace-normal">
          <p className="mb-1 font-semibold text-gray-900">{label}</p>
          {(details || [])?.map((d) => (
            <div key={d?.invoiceId} className="flex items-baseline justify-between gap-3 py-0.5">
              <span className="truncate">{d?.invoiceNumber || t('quittances.table.details.noNumber')}</span>
              <span className="text-right tabular-nums">
                {fmt(d?.amount)}
                {d?.invoiceTotal - d?.amount > 0.005 && (
                  <span className="block text-[10px] text-gray-400">
                    {t('quittances.table.details.remaining', {
                      remaining: fmt(d?.invoiceTotal - d?.amount),
                      total: fmt(d?.invoiceTotal),
                    })}
                  </span>
                )}
              </span>
            </div>
          ))}
          {priorRange && prior > 0.005 && (
            <div className="flex items-baseline justify-between gap-3 py-0.5 text-gray-500">
              <span>
                {priorRange?.from === priorRange?.to
                  ? t('quittances.table.details.previousDp', { number: priorRange?.from })
                  : t('quittances.table.details.previousDps', { from: priorRange?.from, to: priorRange?.to })}
              </span>
              <span className="tabular-nums">{fmt(prior)}</span>
            </div>
          )}
          <div className="mt-1 flex items-baseline justify-between gap-3 border-t border-gray-100 pt-1 font-semibold text-gray-900">
            <span>{t('quittances.table.details.total')}</span>
            <span className="tabular-nums">{fmt(total)}</span>
          </div>
        </div>
      </PortalDropdown>
    </span>
  );
};

// ─── Page ────────────────────────────────────────────────────
const QuittancesPage = () => {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, language } = useTranslation();

  const [project, setProject] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(
    searchParams?.get('tab') === 'quittances' ? 'quittances' : 'denonciations'
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all');

  // Denonciation : modale de creation/edition et suppression
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);     // ligne en cours d'edition
  const [form, setForm] = useState(null);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [orgId, setOrgId] = useState(null);
  const [docsFor, setDocsFor] = useState(null);           // denonciation ouverte en gestion documents
  const [pendingDocDelete, setPendingDocDelete] = useState(null);
  const [deletingDoc, setDeletingDoc] = useState(false);
  // Fichiers deposes AVANT que la denonciation existe. Ils ne partent en
  // stockage qu'apres l'INSERT, une fois l'id connu.
  const [stagedFiles, setStagedFiles] = useState([]);
  // Blocages de suppression d'une denonciation (null = en cours de calcul)
  const [denonBlockers, setDenonBlockers] = useState(null);

  // Panneau "ne pas payer" : la ligne de contrat dont on detaille l'exposition
  const [exposureFor, setExposureFor] = useState(null);

  // Emission d'une quittance
  const [emitFor, setEmitFor] = useState(null);   // ligne de l'onglet Quittances
  const [qForm, setQForm] = useState(null);       // { paymentRequestId, type, montant, dateQuittance }
  const [qError, setQError] = useState('');
  const [emitting, setEmitting] = useState(false);
  const [pendingQDelete, setPendingQDelete] = useState(null);
  const [deletingQ, setDeletingQ] = useState(false);
  const [pdfBusyId, setPdfBusyId] = useState(null);
  // Piece jointe de la quittance de contrat, deposee avant l'insert.
  const [emitStagedFile, setEmitStagedFile] = useState(null);
  const [signedFor, setSignedFor] = useState(null);       // { quittance, row }
  const [uploadingSigned, setUploadingSigned] = useState(false);
  const [pendingSignedDelete, setPendingSignedDelete] = useState(null);
  const [deletingSigned, setDeletingSigned] = useState(false);

  const fetchData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const result = await getProjectQuittancesData(projectId);
      setData(result);
      // Les modales ouvertes tiennent une copie : on la resynchronise apres
      // un ajout ou une suppression de document.
      const sync = (prev) =>
        prev ? (result?.denonciations || [])?.find((d) => d?.id === prev?.id) || null : null;
      setDocsFor(sync);
      setEditing(sync);
      // La modale de quittance signee tient elle aussi une copie de la ligne.
      setSignedFor((prev) =>
        prev
          ? {
              ...prev,
              quittance:
                (result?.quittances || [])?.find((q) => q?.id === prev?.quittance?.id) ||
                prev?.quittance,
            }
          : null
      );
    } catch (err) {
      console.error('[QuittancesPage] fetch error:', err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!projectId) return;
    projectService?.getById(projectId)?.then((p) => { if (p) setProject(p); });
  }, [projectId]);

  // Raison sociale de l'entrepreneur general, pour "Contrat avec" sur une
  // denonciation principale.
  //
  // SOURCE : organizations.name — la meme que la couche PDF via
  // organizationService.getOrgInfo. C'est la seule valeur garantie renseignee
  // et identique pour tous les membres d'une organisation.
  //
  // user_profiles.company n'est PLUS lu : il n'est alimente que pour l'admin
  // fondateur (provision-tenant) et reste NULL pour tout utilisateur invite.
  // Le repli sur full_name a ete RETIRE : inscrire le nom d'une personne a la
  // place de celui de l'entreprise sur une piece juridique est pire que de
  // laisser le champ vide.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { user } } = await supabase?.auth?.getUser();
        if (!user?.id) return;
        const { data: profile } = await supabase
          ?.from('user_profiles')
          ?.select('organization_id')
          ?.eq('id', user?.id)
          ?.single();
        const id = profile?.organization_id || null;
        if (cancelled) return;
        setOrgId(id);
        if (!id) return;
        const { data: org } = await organizationService?.getOrgInfo(id);
        if (!cancelled) setOrgName(org?.name || '');
      } catch {
        // Nom non resolu : le champ restera vide et saisissable.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const rows = data?.subcontracts || [];
  const dpColumns = data?.dpColumns || [];
  // La barre de recherche est commune aux deux onglets : elle doit filtrer
  // les deux. C'est ce qui permet au lien "voir la denonciation" d'arriver
  // directement sur la bonne ligne.
  const denonciations = useMemo(() => {
    const all = data?.denonciations || [];
    const q = searchQuery?.toLowerCase()?.trim();
    if (!q) return all;
    return all?.filter(
      (d) =>
        d?.intervenant_name?.toLowerCase()?.includes(q) ||
        d?.specialite?.toLowerCase()?.includes(q) ||
        d?.lie_a?.toLowerCase()?.includes(q)
    );
  }, [data, searchQuery]);

  // ── Formulaire de denonciation ─────────────────────────────
  const emptyForm = useCallback(() => ({
    subcontractId: '',
    intervenantName: '',
    specialite: '',
    isPrincipal: true,
    // Modele tarifaire du contrat choisi. Il DERIVE la nature de la
    // denonciation ; l'utilisateur ne la choisit pas.
    contractPricingModel: '',
    lieA: orgName,
    dateDenonciation: '',
    montantDenonce: '',
    notes: '',
  }), [orgName]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (d) => {
    setEditing(d);
    setForm({
      subcontractId: d?.subcontract_id || '',
      intervenantName: d?.intervenant_name || '',
      specialite: d?.specialite || '',
      isPrincipal: d?.is_principal !== false,
      // Repris du CONTRAT, pas de d.type_denonciation : la regle est la
      // source de verite, une valeur stockee peut dater d'avant elle.
      contractPricingModel:
        (data?.subcontracts || [])?.find((r) => r?.id === d?.subcontract_id)
          ?.pricingModel || '',
      lieA: d?.lie_a || '',
      dateDenonciation: d?.date_denonciation || '',
      montantDenonce: d?.montant_denonce ?? '',
      notes: d?.notes || '',
    });
    setFormError('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm(null);
    setFormError('');
    setSaving(false);
    setStagedFiles([]);
  };

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  /* ── Options de contrat, PARTAGEES par les deux selecteurs ─────────
   * Une seule source, un seul libelle. Le code de cout est indispensable :
   * un meme sous-traitant peut detenir plusieurs contrats sur un projet, un
   * par code de cout, et "Contrat avec = Untel" ne dirait pas lequel.
   */
  const subcontractOptions = useMemo(
    () =>
      (data?.subcontracts || [])?.map((r) => ({
        value: r?.id,
        label: [r?.name, r?.costCode, r?.trade]?.filter(Boolean)?.join(' · '),
      })),
    [data]
  );

  /**
   * Choix du contrat. Un seul selecteur pour les deux types, mais deux
   * significations :
   *   PRINCIPALE  le contrat du sous-traitant qui se denonce -> on prefille
   *               son nom et son metier, sans les figer.
   *   SECONDAIRE  le contrat du sous-traitant que ce fournisseur
   *               approvisionne -> on NE prefille RIEN : l'intervenant est le
   *               FOURNISSEUR, que l'utilisateur saisit lui-meme.
   * Dans les deux cas lie_a suit le contrat choisi.
   */
  const onPickSubcontract = (subId) => {
    const row = (data?.subcontracts || [])?.find((r) => r?.id === subId);
    setForm((prev) => {
      const principal = prev?.isPrincipal !== false;
      return {
        ...prev,
        subcontractId: subId || '',
        contractPricingModel: row?.pricingModel || '',
        intervenantName: principal
          ? (row?.name && row?.name !== '—' ? row?.name : prev?.intervenantName)
          : prev?.intervenantName,
        specialite: principal ? (row?.trade || prev?.specialite) : prev?.specialite,
        lieA: principal ? orgName : (row?.company || ''),
      };
    });
  };

  /**
   * Changer de type ne change PAS le contrat : c'est le meme lien dans les
   * deux cas. Seule la lecture de lie_a bascule — l'organisation pour une
   * principale, la raison sociale du contrat pour une secondaire.
   */
  const onToggleType = (isPrincipal) => {
    setForm((prev) => {
      const row = (data?.subcontracts || [])?.find((r) => r?.id === prev?.subcontractId);

      // En secondaire, l'intervenant est le FOURNISSEUR : le nom du
      // sous-traitant n'a plus rien a y faire. On ne le vide que s'il vient
      // encore du prefill automatique du contrat — si l'utilisateur a saisi
      // autre chose, sa saisie est conservee.
      const typed = (prev?.intervenantName || '')?.trim();
      const fromPrefill =
        typed !== '' && (typed === (row?.name || '')?.trim() || typed === (row?.company || '')?.trim());
      const intervenantName =
        !isPrincipal && fromPrefill ? '' : prev?.intervenantName;

      return {
        ...prev,
        isPrincipal,
        subcontractId: prev?.subcontractId || '',
        intervenantName,
        lieA: isPrincipal ? orgName : (row?.company || ''),
      };
    });
  };

  // Contrat selectionne, source des valeurs derivees.
  const selectedSubcontract = useMemo(
    () => (data?.subcontracts || [])?.find((r) => r?.id === form?.subcontractId) || null,
    [data, form?.subcontractId]
  );

  /**
   * lie_a n'est PLUS saisi : il reflete le contrat.
   *   PRINCIPALE  la raison sociale de notre organisation
   *   SECONDAIRE  subcontracts.company du contrat vise
   * On lit `company` (porte par le contrat) et non `name` (qui vient de
   * subcontractors.company_name) : c'est `company` qui a servi au backfill.
   */
  const derivedLieA = form?.isPrincipal
    ? orgName
    : (selectedSubcontract?.company || '');

  /**
   * Fourniture ouverte : le fournisseur livre au fur et a mesure, le montant
   * peut legitimement rester inconnu.
   *
   * DERIVEE du contrat, jamais choisie. Forcee a false en secondaire : le
   * modele tarifaire decrit le contrat du sous-traitant, pas l'entente de
   * son fournisseur. Meme garde que dans toDenonciationPayload.
   */
  const isOuverte =
    form?.isPrincipal === false
      ? false
      : deriveTypeDenonciation(form?.contractPricingModel) === 'ouverte';

  /* ── Fichiers en attente (mode creation uniquement) ────────
   * DocumentAttach ne connait pas la source des donnees : on lui presente
   * les fichiers en attente sous la forme de pseudo-documents. Il les
   * affiche dans la meme liste et, surtout, sa detection de doublons
   * s'applique a eux sans qu'il faille toucher au composant partage.
   */
  const stagedDocs = useMemo(
    () =>
      stagedFiles?.map((f, i) => ({
        id: `staged-${i}-${f?.name}`,
        file_name: f?.name,
        mime_type: f?.type,
        size_bytes: f?.size,
      })),
    [stagedFiles]
  );

  const stageFile = (file) => {
    if (!file) return;
    // Meme validation qu'au televersement : autant refuser tout de suite que
    // laisser l'utilisateur enregistrer avant de decouvrir le rejet.
    const problem = validateLettreFile(file);
    if (problem) {
      showToast(
        'error',
        problem === 'FILE_TOO_LARGE'
          ? t('quittances.toolbar.lettreTooLarge')
          : t('quittances.toolbar.lettreInvalidType')
      );
      return;
    }
    setStagedFiles((prev) => [...prev, file]);
  };

  // Un fichier pas encore televerse se retire sans confirmation : il n'y a
  // rien a perdre.
  const unstageFile = (doc) =>
    setStagedFiles((prev) => prev?.filter((f, i) => `staged-${i}-${f?.name}` !== doc?.id));

  const handleSave = async () => {
    if (saving) return;
    if (!form?.intervenantName?.trim()) {
      setFormError(t('quittances.toolbar.required'));
      return;
    }
    // Le contrat est le seul lien du modele, et NOT NULL en base : sans lui
    // la denonciation ne peut bloquer aucun paiement.
    if (!form?.subcontractId) {
      setFormError(t('quittances.toolbar.contractRequired'));
      return;
    }
    // denonciations_montant_ferme_check rejetterait l'insert avec une erreur
    // brute : on tranche ici, dans la langue de l'utilisateur.
    if (!isOuverte && String(form?.montantDenonce ?? '')?.trim() === '') {
      setFormError(t('quittances.toolbar.montantRequired'));
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      // lie_a est DERIVE, jamais ce qui traine dans l'etat : subcontract_id
      // est la source de verite, lie_a n'en est que le reflet lisible.
      // Une fiche ancienne mal renseignee est ainsi corrigee des sa
      // prochaine sauvegarde.
      const payload = { ...form, lieA: derivedLieA };

      // En edition, les documents sont deja partis au fil de l'eau.
      // A la creation, il faut d'abord l'id : on insere, PUIS on televerse.
      let createdId = null;
      if (editing) {
        await updateDenonciation(editing?.id, payload);
      } else {
        const created = await createDenonciation(projectId, payload);
        createdId = created?.id || null;
      }

      // Televersement des fichiers mis en attente. La denonciation est deja
      // enregistree : un fichier qui echoue ne doit JAMAIS la faire perdre.
      const failed = [];
      if (createdId && stagedFiles?.length > 0) {
        for (const file of stagedFiles) {
          try {
            await addDocument({ orgId, projectId, denonciationId: createdId, file });
          } catch (err) {
            console.error('[QuittancesPage] staged upload error:', file?.name, err);
            failed?.push(file?.name);
          }
        }
      }

      showToast(
        failed?.length > 0 ? 'error' : 'success',
        failed?.length > 0
          ? t('quittances.toolbar.docsUploadFailed', { names: failed?.join(', ') })
          : editing
            ? t('quittances.toolbar.updatedToast')
            : t('quittances.toolbar.createdToast')
      );
      closeForm();
      await fetchData();
    } catch (err) {
      console.error('[QuittancesPage] save error:', err);
      setSaving(false);
      setFormError(t('quittances.toolbar.errorToast'));
    }
  };

  // Ouvre la lettre dans un onglet via une URL signee (bucket prive).
  const openDoc = async (doc) => {
    const url = await getLettreSignedUrl(doc?.file_path || doc);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
    else showToast('error', t('quittances.toolbar.errorToast'));
  };

  // ── Documents d'une denonciation ───────────────────────────
  // Televersement direct depuis la colonne : pas de modale.
  // Televersement d'UN fichier. Pas de rafraichissement ni de toast ici : le
  // lot peut en contenir plusieurs, et rafraichir a chaque fichier faisait
  // recharger toute la page. Le bilan est fait dans handleAttachComplete.
  const attachResult = useRef({ ok: 0, failed: 0 });

  const handleAttach = async (denonciation, file) => {
    if (!file) return;
    const problem = validateLettreFile(file);
    if (problem) {
      attachResult.current.failed += 1;
      showToast(
        'error',
        problem === 'FILE_TOO_LARGE'
          ? t('quittances.toolbar.lettreTooLarge')
          : t('quittances.toolbar.lettreInvalidType')
      );
      return;
    }
    try {
      await addDocument({
        orgId,
        projectId,
        denonciationId: denonciation?.id,
        file,
      });
      attachResult.current.ok += 1;
    } catch (err) {
      console.error('[QuittancesPage] attach error:', err);
      attachResult.current.failed += 1;
    }
  };

  // Un seul rafraichissement, un seul message, a la fin du lot.
  const handleAttachComplete = async () => {
    const { ok, failed } = attachResult.current;
    attachResult.current = { ok: 0, failed: 0 };
    if (ok > 0) await fetchData();
    if (failed > 0) showToast('error', t('quittances.toolbar.errorToast'));
    else if (ok > 0) showToast('success', t('quittances.toolbar.docsAdded'));
  };

  const confirmDocDelete = async () => {
    if (!pendingDocDelete || deletingDoc) return;
    setDeletingDoc(true);
    try {
      await removeDocument(pendingDocDelete?.id, pendingDocDelete?.file_path);
      showToast('success', t('quittances.toolbar.docsRemoved'));
      await fetchData();
    } catch (err) {
      console.error('[QuittancesPage] doc delete error:', err);
      showToast('error', t('quittances.toolbar.errorToast'));
    } finally {
      setDeletingDoc(false);
      setPendingDocDelete(null);
    }
  };

  /* ── Suppression d'une denonciation ────────────────────────
   * quittances.denonciation_id est en ON DELETE RESTRICT : sans ce controle
   * la base rendrait un 23503 illisible. On compte d'abord.
   */
  const askDeleteDenonciation = async (d) => {
    setPendingDelete(d);
    setDenonBlockers(null);
    try {
      setDenonBlockers(await getDenonciationDeleteBlockers(d?.id));
    } catch (err) {
      console.error('[QuittancesPage] blockers error:', err);
      setDenonBlockers({ quittances: 0, documents: 0, total: 0 });
    }
  };

  const denonIsBlocked = (denonBlockers?.total || 0) > 0;

  const confirmDelete = async () => {
    if (!pendingDelete || deleting) return;
    // Second garde-fou : le bouton est deja neutralise, mais on ne compte
    // pas sur l'interface pour proteger une piece juridique.
    if (denonIsBlocked) {
      setPendingDelete(null);
      setDenonBlockers(null);
      return;
    }
    setDeleting(true);
    try {
      await deleteDenonciation(pendingDelete?.id);
      showToast('success', t('quittances.toolbar.deletedToast'));
      await fetchData();
    } catch (err) {
      console.error('[QuittancesPage] delete error:', err);
      showToast('error', t('quittances.toolbar.errorToast'));
    } finally {
      setDeleting(false);
      setPendingDelete(null);
      setDenonBlockers(null);
    }
  };

  /* ─────────────────────────────────────────────────────────
   * Quittances
   *
   * Le montant est FIGE a l'emission : ce qui est affiche sur la pastille et
   * dans le PDF vient de la ligne en base, jamais d'un recalcul. Une facture
   * approuvee plus tard ne doit pas modifier une quittance deja signee.
   * ───────────────────────────────────────────────────────── */
  /**
   * Quittances appartenant a un FOURNISSEUR denonce. Elles portent le
   * subcontract_id du sous-traitant (colonne NOT NULL) mais ne le concernent
   * pas : elles sont exclues de ses pastilles, de ses cumuls, de ses cases
   * vertes et de son statut. Elles n'apparaissent que dans le marqueur ⚠.
   */
  const supplierQIds = useMemo(
    () => new Set(data?.supplierQuittanceIds || []),
    [data]
  );

  const quittancesBySub = useMemo(() => {
    const by = {};
    for (const q of data?.quittances || []) {
      if (supplierQIds?.has(q?.id)) continue;
      const k = q?.subcontract_id;
      if (!k) continue;
      if (!by?.[k]) by[k] = [];
      by?.[k]?.push(q);
    }
    return by;
  }, [data, supplierQIds]);

  // Couples (contrat, DP) portant une quittance. Sert a verdir la cellule
  // correspondante. Les quittances de fournisseur (subcontract_id NULL) en
  // sont exclues : elles ne couvrent pas une DP de notre contrat.
  /**
   * Couples (contrat, DP) portant une quittance.
   *
   * Toute quittance du sous-traitant compte, partielle comme finale : la
   * question posee par la case est "ce versement est-il quittance ?", et une
   * finale l'atteste aussi. Les quittances de FOURNISSEUR sont exclues —
   * elles portent le subcontract_id du sous-traitant sans le liberer.
   *
   * Une quittance sans payment_request_id ne peut etre attribuee a aucune
   * colonne : elle alimente le seau "Sans DP".
   */
  const dpCoveredByQuittance = useMemo(() => {
    const set = new Set();
    for (const q of data?.quittances || []) {
      if (supplierQIds?.has(q?.id)) continue;
      if (!q?.subcontract_id) continue;
      set?.add(`${q?.subcontract_id}::${q?.payment_request_id || NO_DP_COLUMN_ID}`);
    }
    return set;
  }, [data, supplierQIds]);

  /**
   * Montant quittance par (contrat, DP) : le plus grand montant des quittances
   * du sous-traitant sur cette DP. Sert a voir si le paye cumule a depasse ce
   * qui a ete signe (versement fait apres la signature).
   */
  const releasedBySubDp = useMemo(() => {
    const map = new Map();
    for (const q of data?.quittances || []) {
      if (supplierQIds?.has(q?.id)) continue;
      if (!q?.subcontract_id) continue;
      const key = `${q?.subcontract_id}::${q?.payment_request_id || NO_DP_COLUMN_ID}`;
      map?.set(key, Math.max(map?.get(key) || 0, Number(q?.montant) || 0));
    }
    return map;
  }, [data, supplierQIds]);

  /**
   * Quittance FINALE de chaque contrat (id du contrat -> son montant). Une
   * finale solde tout le contrat : dans les colonnes "Quittance DPn", elle
   * couvre TOUTES les DP payees, pas seulement celle a laquelle elle est
   * rattachee. Une partielle ne couvre que sa DP.
   */
  const finaleBySub = useMemo(() => {
    const map = new Map();
    for (const q of data?.quittances || []) {
      if (supplierQIds?.has(q?.id)) continue;
      if (!q?.subcontract_id || q?.type !== 'finale') continue;
      map?.set(q?.subcontract_id, Math.max(map?.get(q?.subcontract_id) || 0, Number(q?.montant) || 0));
    }
    return map;
  }, [data, supplierQIds]);

  // DP proposees : celles qui portent des montants approuves, plus "Sans DP".
  // C'est exactement l'ensemble des colonnes du tableau, pour que le montant
  // suggere corresponde toujours a une colonne visible.
  const dpOptions = useMemo(
    () =>
      (dpColumns || [])?.map((dp) =>
        dp?.isNoDp
          ? { value: NO_DP_VALUE, key: NO_DP_COLUMN_ID, isNoDp: true }
          : { value: dp?.id, key: dp?.id, number: dp?.number }
      ),
    [dpColumns]
  );

  /**
   * Montant d'une quittance — CALCULE, jamais saisi.
   *   partielle : cumul approuve jusqu'a la DP choisie ("Sans DP" reste isole)
   *   finale    : tout l'approuve du contrat, toutes DP confondues, y compris
   *               les factures hors DP. Elle solde, donc elle ne depend plus
   *               de la DP affichee.
   */
  const suggestedAmount = useCallback((row, dpValue, type) => {
    if (!row) return 0;
    // MEME SOURCE QUE LE TABLEAU : l'argent reellement verse. Une quittance
    // decharge un paiement, pas une approbation. perDpPaid et paidToDate sont
    // deja sur la ligne — aucune requete ajoutee, seule la source lue change.
    if (type === 'finale') return row?.paidToDate || 0;
    const key = dpValue === NO_DP_VALUE ? NO_DP_COLUMN_ID : dpValue;
    const fromColumn = row?.perDpPaid?.[key];
    if (fromColumn != null) return fromColumn;
    return row?.paidToDate || 0;
  }, []);

  // Derniere DP portant un VERSEMENT pour ce contrat ; le cumul paye y est
  // maximal. Sur perDp (approuve), on pre-selectionnait une DP que
  // emittableDpOptions ecarte desormais : le selecteur aurait porte une
  // valeur absente de sa propre liste.
  const defaultDpValue = useCallback(
    (row) => {
      const real = dpOptions?.filter((o) => !o?.isNoDp);
      for (let i = real?.length - 1; i >= 0; i -= 1) {
        if ((row?.perDpPaid?.[real?.[i]?.value] || 0) > 0) return real?.[i]?.value;
      }
      return NO_DP_VALUE;
    },
    [dpOptions]
  );

  /**
   * Anti-doublon. Deux regles :
   *   - une seule quittance par couple (contrat, DP), "Sans DP" compris ;
   *   - une seule quittance FINALE par contrat.
   * Retourne la quittance fautive, pour pouvoir l'ouvrir directement.
   */
  const findDuplicate = useCallback(
    (row, dpValue, type) => {
      const list = quittancesBySub?.[row?.id] || [];
      if (type === 'finale') {
        const existing = list?.find((q) => q?.type === 'finale');
        if (existing) return { reason: 'finale', quittance: existing };
      }
      // Le test par DP ne vaut QUE pour les partielles. Une finale suit
      // presque toujours des partielles sur la meme DP — c'est le cas normal :
      // on emet au fil des DP, puis on solde. Son unicite est deja garantie
      // par le test ci-dessus.
      if (type !== 'finale') {
        const target = dpValue === NO_DP_VALUE ? null : dpValue;
        const same = list?.find((q) => (q?.payment_request_id || null) === target);
        if (same) return { reason: 'dp', quittance: same };
      }
      return null;
    },
    [quittancesBySub]
  );

  /**
   * Ouvre la quittance rattachee. Plus rien n'est genere : une quittance est
   * un document que l'entrepreneur fournit, chaque client SaaS ayant ses
   * propres formulaires.
   * Sans piece jointe, le clic ouvre directement la fenetre de depot — c'est
   * l'action utile a ce moment-la.
   */
  const openQuittanceDoc = async (quittance, row) => {
    if (!quittance?.lettre_url) {
      setSignedFor({ quittance, row });
      return;
    }
    if (pdfBusyId) return;
    setPdfBusyId(quittance?.id);
    try {
      const url = await getQuittanceSigneeUrl(quittance?.lettre_url);
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
      else showToast('error', t('quittances.toolbar.errorToast'));
    } catch (err) {
      console.error('[QuittancesPage] open quittance doc error:', err);
      showToast('error', t('quittances.toolbar.errorToast'));
    } finally {
      setPdfBusyId(null);
    }
  };

  const openEmit = (row) => {
    const dpValue = defaultDpValue(row);
    setEmitFor(row);
    setQForm({
      // null = le sous-traitant emet. Un uuid = son fournisseur denonce.
      issuerDenonciationId: null,
      supplierMontant: '',
      paymentRequestId: dpValue,
      type: 'partielle',
      // Plus de `montant` : il est CALCULE, pas saisi.
      dateQuittance: todayIso(),
      notes: '',
    });
    setQError('');
    setEmitting(false);
    setEmitStagedFile(null);
  };

  const closeEmit = () => {
    setEmitFor(null);
    setQForm(null);
    setQError('');
    setEmitting(false);
    setEmitStagedFile(null);
  };

  // Changer de DP change le cumul : le montant suggere est recalcule, et
  // l'avertissement "montant modifie" repart de zero.
  const onPickDp = (dpValue) => {
    setQForm((prev) => ({ ...prev, paymentRequestId: dpValue }));
    setQError('');
  };

  /**
   * Bascule de type. La DP et le type sont INDEPENDANTS : passer a « Finale »
   * garde la DP choisie (elle est ecrite en base avec la finale) ; seul le
   * montant change — la finale solde tout le paye du contrat.
   *
   * Retour a « Partielle » : la DP gardee doit encore etre emettable en
   * partielle (nouvel argent verse, pas deja quittancee). Sinon, on revient a
   * la DP proposee a l'ouverture, pour que le selecteur garde une valeur de sa liste.
   */
  const onPickQuittanceType = (type) => {
    setQForm((prev) => {
      if (type === 'finale') return { ...prev, type };
      const key = prev?.paymentRequestId === NO_DP_VALUE ? NO_DP_COLUMN_ID : prev?.paymentRequestId;
      const stillValid =
        !dpCoveredByQuittance?.has(`${emitFor?.id}::${key}`) && dpIncrement(emitFor, prev?.paymentRequestId) > 0;
      return { ...prev, type, paymentRequestId: stillValid ? prev?.paymentRequestId : defaultDpValue(emitFor) };
    });
    setQError('');
  };

  // Source UNIQUE du montant : affichage, garde du zero et ecriture en base.
  const computedMontant = useMemo(
    () => (emitFor && qForm ? suggestedAmount(emitFor, qForm?.paymentRequestId, qForm?.type) : 0),
    [emitFor, qForm, suggestedAmount]
  );

  // Un document legal a 0 $ n'eteint rien : on ne l'emet pas.
  const montantIsZero = !(computedMontant > 0);

  /**
   * Infobulle du marqueur ⚠ : un fournisseur par ligne, avec ce qui reste a
   * couvrir. Garde != null avant tout fmt — un montant denonce peut etre NULL
   * sur une denonciation "ouverte", et fmt(null) rendrait "0,00 $".
   */
  const supplierTooltip = useCallback(
    (row) =>
      (row?.supplier_details || [])
        ?.filter((sd) => sd?.status !== 'covered')
        ?.map((sd) => {
          const denounced =
            sd?.montant_denonce != null
              ? fmt(sd?.montant_denonce)
              : t('quittances.toolbar.noAmount');
          const remaining =
            sd?.montant_denonce != null
              ? fmt(Math.max(0, sd?.montant_denonce - (sd?.quittance_total || 0)))
              : t('quittances.toolbar.noAmount');
          return t('quittances.exposure.markerTooltip', {
            name: sd?.intervenant_name,
            denounced,
            remaining,
          });
        })
        ?.join('\n'),
    [t]
  );

  /**
   * CHEMIN FOURNISSEUR. issuerDenonciationId : null = le sous-traitant,
   * un uuid = ce fournisseur denonce.
   *
   * computedMontant et les regles A/B ne sont PAS reecrits : ils cessent
   * simplement d'etre lus. Le risque de regression tient a trois conditions,
   * toutes sur ce seul booleen.
   */
  const isSupplierIssuer = !!qForm?.issuerDenonciationId;

  const issuerDetail = useMemo(
    () =>
      (emitFor?.supplier_details || [])?.find(
        (sd) => sd?.denonciation_id === qForm?.issuerDenonciationId
      ) || null,
    [emitFor, qForm?.issuerDenonciationId]
  );

  // Saisie libre : nous n'avons aucune facture de ce fournisseur, rien n'est
  // calculable. Seul garde-fou, le zero — un document legal vide n'eteint rien.
  const supplierMontant = parseFloat(qForm?.supplierMontant);
  const supplierMontantIsZero = !(supplierMontant > 0);

  /**
   * REGLE A — une partielle exige du NOUVEL ARGENT VERSE.
   * perDpPaid est cumulatif dans l'ordre des DP reelles : l'apport d'une DP
   * est son cumul moins celui de la precedente. Une DP ou rien n'a ete verse
   * ne se quittance pas — on ne decharge pas de l'argent qu'on n'a pas paye.
   *
   * "Sans DP" fait exception : son total est ISOLE, pas cumulatif. Son apport
   * est donc sa propre valeur.
   */
  const dpIncrement = useCallback(
    (row, dpValue) => {
      if (!row) return 0;
      if (dpValue === NO_DP_VALUE) return row?.perDpPaid?.[NO_DP_COLUMN_ID] || 0;
      const real = dpOptions?.filter((o) => !o?.isNoDp);
      const idx = real?.findIndex((o) => o?.value === dpValue);
      if (idx == null || idx < 0) return 0;
      const current = row?.perDpPaid?.[dpValue] || 0;
      const previous = idx === 0 ? 0 : (row?.perDpPaid?.[real?.[idx - 1]?.value] || 0);
      return current - previous;
    },
    [dpOptions]
  );

  /**
   * REGLE B — une finale exige un solde nul.
   * Exemptions : la regie (balance null) et le bon de commande
   * (contractValue null) n'ont aucun montant contractuel de reference —
   * "facturer exactement son contrat" n'y a pas de sens.
   */
  const balanceUnverifiable =
    emitFor != null && (emitFor?.balance == null || emitFor?.contractValue == null);

  const finaleBlocked =
    qForm?.type === 'finale' && !balanceUnverifiable && !isSettled(emitFor?.balance);

  const partielleBlocked =
    qForm?.type !== 'finale' &&
    !montantIsZero &&
    !(dpIncrement(emitFor, qForm?.paymentRequestId) > 0);

  // Chemin fournisseur : AUCUNE regle dure. Un fournisseur peut denoncer
  // 50 000 et ne livrer que 30 000 — on informe, on ne bloque pas. Les regles
  // A et B sont court-circuitees, jamais reecrites.
  const emitBlocked = isSupplierIssuer
    ? supplierMontantIsZero
    : montantIsZero || finaleBlocked || partielleBlocked;

  /**
   * Libelle d'une pastille : le NUMERO, pas le montant. Celui-ci vit deja
   * dans la colonne de la DP — le repeter est ce qui triplait la hauteur des
   * lignes.
   */
  const quittanceChipLabel = useCallback(
    (q, isSupplier = false) => {
      // Un fournisseur n'a pas de DP : le numero n'existe pas, et "Sans DP"
      // n'aurait aucun sens. Le MONTANT devient le seul discriminateur —
      // sans lui, deux partielles porteraient le meme libelle.
      if (isSupplier) {
        const type = t(`quittances.emit.type.${q?.type}`);
        return q?.montant != null ? `${type} · ${fmt(q?.montant)}` : type;
      }
      if (q?.type === 'finale') return t('quittances.emit.chipFinale');
      const dp = (data?.paymentRequests || [])?.find((d) => d?.id === q?.payment_request_id);
      return dp?.number != null
        ? t('quittances.emit.chipNumbered', { number: dp?.number })
        : t('quittances.table.noDp');
    },
    [data, t]
  );

  /**
   * Pastille de quittance, partagee par la ligne du sous-traitant et par le
   * panneau d'exposition. Un seul rendu, deux libelles.
   */
  const QuittanceChip = useCallback(
    ({ quittance, onOpen, isSupplier = false }) => (
      <button
        type="button"
        onClick={onOpen}
        title={
          quittance?.lettre_url
            ? t('quittances.signed.present')
            : t('quittances.signed.attach')
        }
        className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[10px] font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 ${
          quittance?.type === 'finale'
            ? 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100'
            : 'bg-orange-50 border-orange-200 text-primary hover:bg-orange-100'
        }`}
      >
        {quittanceChipLabel(quittance, isSupplier)}
        {quittance?.lettre_url && (
          <Icon name="Paperclip" size={10} className="text-green-600 shrink-0" />
        )}
      </button>
    ),
    [quittanceChipLabel, t]
  );

  /**
   * Unicite de la FINALE sur le chemin fournisseur. Plusieurs partielles
   * restent permises — un fournisseur peut etre paye en plusieurs versements.
   * AUCUN test par DP : il n'a pas de demande de paiement.
   */
  const findSupplierDuplicate = useCallback(
    (row, denonciationId, type) => {
      if (type !== 'finale') return null;
      const sd = (row?.supplier_details || [])?.find(
        (x) => x?.denonciation_id === denonciationId
      );
      const existing = (sd?.quittances || [])?.find((q) => q?.type === 'finale');
      return existing ? { reason: 'finaleSupplier', quittance: existing } : null;
    },
    []
  );

  /**
   * DP reellement emettables pour CE contrat : celles qui ont apporte des
   * factures (regle A) et qui ne sont pas deja quittancees.
   *
   * Exception FINALE : elle solde tout le contrat, quelle que soit la DP a
   * laquelle on la rattache. Toutes les DP du tableau sont donc proposees,
   * y compris une DP deja quittancee en partielle (cas normal : on emet au fil
   * des DP, puis on solde).
   */
  const emittableDpOptions = useMemo(() => {
    if (!emitFor) return [];
    if (qForm?.type === 'finale') return dpOptions;
    return dpOptions?.filter((o) => {
      const key = o?.isNoDp ? NO_DP_COLUMN_ID : o?.value;
      if (dpCoveredByQuittance?.has(`${emitFor?.id}::${key}`)) return false;
      return dpIncrement(emitFor, o?.value) > 0;
    });
  }, [emitFor, qForm?.type, dpOptions, dpCoveredByQuittance, dpIncrement]);

  const handleEmit = async () => {
    if (emitting || !emitFor || !qForm) return;
    const duplicate = findDuplicate(emitFor, qForm?.paymentRequestId, qForm?.type);
    if (duplicate) return; // la modale affiche deja le blocage
    if (emitBlocked) {
      setQError(
        isSupplierIssuer
          ? t('quittances.emit.supplierAmountRequired')
          : montantIsZero
            ? t('quittances.emit.amountRequired')
            : finaleBlocked
              ? t('quittances.emit.finaleNeedsBalance', { amount: fmt(emitFor?.balance) })
              : t('quittances.emit.dpNoNewInvoice')
      );
      return;
    }
    const montant = isSupplierIssuer ? supplierMontant : computedMontant;
    setEmitting(true);
    setQError('');
    try {
      const created = await createQuittance(projectId, {
        // subcontract_id est NOT NULL et vaut le contrat dans les deux cas :
        // c'est bien lui qui est en jeu, meme quand c'est le fournisseur qui
        // quittance. Le discriminateur est la denonciation.
        subcontractId: emitFor?.id,
        // Un fournisseur n'a pas de demande de paiement.
        paymentRequestId: isSupplierIssuer
          ? null
          : (qForm?.paymentRequestId === NO_DP_VALUE ? null : qForm?.paymentRequestId),
        type: qForm?.type,
        montant,
        dateQuittance: qForm?.dateQuittance || null,
        notes: qForm?.notes,
        // Fournisseur -> SA denonciation secondaire.
        // Sous-traitant -> R4, la denonciation PRINCIPALE du contrat.
        denonciationId: isSupplierIssuer
          ? qForm?.issuerDenonciationId
          : (emitFor?.denonciationPrincipale?.id ?? null),
      });
      // La piece signee part APRES l'insert : son chemin a besoin de l'id.
      // Un echec de televersement ne doit pas faire perdre la quittance.
      let uploadFailed = null;
      if (created?.id && emitStagedFile) {
        try {
          await uploadQuittanceSignee({
            orgId,
            projectId,
            quittanceId: created?.id,
            file: emitStagedFile,
            previousPath: null,
          });
        } catch (err) {
          console.error('[QuittancesPage] emit upload error:', err);
          uploadFailed = emitStagedFile?.name;
        }
      }

      closeEmit();
      showToast(
        uploadFailed ? 'error' : 'success',
        uploadFailed
          ? t('quittances.supplier.savedButUploadFailed', { name: uploadFailed })
          : t('quittances.emit.createdToast')
      );
      await fetchData();
    } catch (err) {
      console.error('[QuittancesPage] emit error:', err);
      setEmitting(false);
      setQError(t('quittances.toolbar.errorToast'));
    }
  };

  /* ── Quittance signee ──────────────────────────────────────
   * Un seul fichier par quittance : il vit dans quittances.lettre_url, pas
   * dans une table de documents. On le presente a DocumentAttach sous la
   * forme d'un pseudo-document pour reutiliser la meme zone de depot que
   * partout ailleurs dans l'app.
   */
  const signedDocOf = (quittance) =>
    quittance?.lettre_url
      ? [{
          id: `${quittance?.id}-signed`,
          file_name: baseName(quittance?.lettre_url),
          file_path: quittance?.lettre_url,
        }]
      : [];

  const handleAttachSigned = async (file) => {
    if (!file || !signedFor) return;
    const problem = validateLettreFile(file);
    if (problem) {
      showToast(
        'error',
        problem === 'FILE_TOO_LARGE'
          ? t('quittances.toolbar.lettreTooLarge')
          : t('quittances.toolbar.lettreInvalidType')
      );
      return;
    }
    setUploadingSigned(true);
    try {
      await uploadQuittanceSignee({
        orgId,
        projectId,
        quittanceId: signedFor?.quittance?.id,
        file,
        previousPath: signedFor?.quittance?.lettre_url || null,
      });
      showToast('success', t('quittances.signed.uploadedToast'));
    } catch (err) {
      console.error('[QuittancesPage] signed upload error:', err);
      showToast('error', t('quittances.toolbar.errorToast'));
    } finally {
      setUploadingSigned(false);
    }
  };

  const confirmSignedDelete = async () => {
    if (!pendingSignedDelete || deletingSigned) return;
    setDeletingSigned(true);
    try {
      await removeQuittanceSignee(
        pendingSignedDelete?.quittance?.id,
        pendingSignedDelete?.quittance?.lettre_url
      );
      showToast('success', t('quittances.signed.removedToast'));
      await fetchData();
    } catch (err) {
      console.error('[QuittancesPage] signed delete error:', err);
      showToast('error', t('quittances.toolbar.errorToast'));
    } finally {
      setDeletingSigned(false);
      setPendingSignedDelete(null);
    }
  };

  const confirmQuittanceDelete = async () => {
    if (!pendingQDelete || deletingQ) return;
    setDeletingQ(true);
    try {
      await deleteQuittance(pendingQDelete?.quittance?.id);
      showToast('success', t('quittances.emit.deletedToast'));
      await fetchData();
    } catch (err) {
      console.error('[QuittancesPage] quittance delete error:', err);
      showToast('error', t('quittances.toolbar.errorToast'));
    } finally {
      setDeletingQ(false);
      setPendingQDelete(null);
    }
  };

  // ── KPI ────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const totalSubs = rows?.length || 0;
    const denounced = rows?.filter((r) => !!r?.denonciation)?.length || 0;
    const approvedToDate = rows?.reduce((s, r) => s + (r?.approvedToDate || 0), 0);
    // Les contrats en regie n'ont pas de montant revise : ils sortent du solde.
    const balance = rows
      ?.filter((r) => r?.revisedContract !== null)
      ?.reduce((s, r) => s + (r?.revisedContract - r?.approvedToDate), 0);
    // Contrats qu'un fournisseur denonce non quittance empeche de payer.
    const blocked = rows?.filter((r) => r?.payment_blocked)?.length || 0;
    return { totalSubs, denounced, approvedToDate, balance, blocked };
  }, [rows]);

  // ── Filtres de l'onglet Quittances ─────────────────────────
  const visibleRows = useMemo(() => {
    let out = rows;
    if (filter === 'notified') out = out?.filter((r) => !!r?.denonciation);
    else if (filter === 'notNotified') out = out?.filter((r) => !r?.denonciation);
    else if (filter === 'settled')
      out = out?.filter(
        (r) =>
          isSettled(r?.balance) ||
          (quittancesBySub?.[r?.id] || [])?.some((q) => q?.type === 'finale')
      );
    const q = searchQuery?.toLowerCase()?.trim();
    if (q) {
      out = out?.filter(
        (r) =>
          r?.name?.toLowerCase()?.includes(q) ||
          r?.trade?.toLowerCase()?.includes(q)
      );
    }
    return out;
  }, [rows, filter, searchQuery, quittancesBySub]);

  const [toastMsg, setToastMsg] = useState(null);
  const showToast = (type, text) => {
    setToastMsg({ type, text });
    setTimeout(() => setToastMsg(null), 3500);
  };

  const tabs = [
    { id: 'denonciations', label: t('quittances.tabs.denonciations') },
    { id: 'quittances', label: t('quittances.tabs.quittances') },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Icon name="Loader2" size={48} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="pt-0 pb-0 px-0 h-full flex flex-col">
      {/* ── En-tete + KPI ── */}
      <div className="mb-4">
        <div className="flex items-start justify-between gap-6">
          <div className="flex-shrink-0">
            <div className="flex items-center gap-2 mb-1">
              <Icon name="ShieldCheck" size={20} className="text-primary" />
              <h1 className="text-lg font-semibold text-[#2C3E50]">{t('quittances.title')}</h1>
            </div>
            <div className="flex items-center gap-1 mb-1">
              <span
                onClick={() => navigate('/project-detail')}
                className="text-sm text-primary font-normal hover:text-primary-dark cursor-pointer transition-colors"
              >
                {project?.projectNumber || t('common.na')}
              </span>
              <span className="text-sm text-primary font-normal">-</span>
              <span
                onClick={() => navigate('/project-detail')}
                className="text-sm text-primary font-normal hover:text-primary-dark cursor-pointer transition-colors"
              >
                {project?.name || t('common.loading')}
              </span>
            </div>
            <p className="text-xs text-gray-500">{t('quittances.subtitle')}</p>
          </div>
          <div className="flex-1 flex items-center justify-end gap-2">
            <StatsCard
              title={t('quittances.kpi.subcontractors')}
              value={kpis?.totalSubs}
              sublabel=""
              iconName="Users"
              iconColor="text-blue-500"
              valueColor="text-blue-600"
              titleWeight="font-medium"
            />
            <StatsCard
              title={t('quittances.kpi.denounced')}
              value={`${kpis?.denounced} / ${kpis?.totalSubs}`}
              sublabel=""
              iconName="FileCheck"
              iconColor="text-gray-400"
              valueColor="text-gray-900"
              titleWeight="font-medium"
            />
            <StatsCard
              title={t('quittances.kpi.approvedToDate')}
              /* Les mots seuls, pas de second montant : c'est ce qui
                 rendait ces deux tuiles plus larges que les trois autres. */
              value={
                <ValueWithNote
                  amount={fmt(kpis?.approvedToDate)}
                  note={t('quittances.table.taxIncl')}
                />
              }
              sublabel=""
              iconName="Banknote"
              iconColor="text-emerald-500"
              valueColor="text-emerald-700"
              titleWeight="font-medium"
            />
            <StatsCard
              title={t('quittances.kpi.balance')}
              value={
                <ValueWithNote
                  amount={fmt(kpis?.balance)}
                  note={t('quittances.table.taxIncl')}
                />
              }
              sublabel=""
              iconName="Scale"
              iconColor="text-amber-500"
              valueColor="text-amber-700"
              titleWeight="font-medium"
            />
            <StatsCard
              title={t('quittances.kpi.blocked')}
              value={kpis?.blocked}
              sublabel=""
              iconName="ShieldAlert"
              iconColor={kpis?.blocked > 0 ? 'text-red-500' : 'text-gray-400'}
              valueColor={kpis?.blocked > 0 ? 'text-red-600' : 'text-gray-900'}
              titleWeight="font-medium"
            />
          </div>
        </div>
      </div>

      {/* ── Onglets ── */}
      <div className="mb-4">
        <div className="flex items-center gap-1 bg-white rounded-lg border border-gray-200 p-1 shadow-sm w-fit">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                activeTab === tab?.id
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              {tab?.label}
            </button>
          ))}
        </div>

        {/* ── Barre d'outils ── */}
        <div className="flex items-center justify-between gap-3 mt-3">
          <div className="flex items-center gap-2">
            <select
              value={filter}
              onChange={(e) => setFilter(e?.target?.value)}
              className="h-8 px-2 text-xs rounded-md border border-gray-200 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
            >
              <option value="all">{t('quittances.toolbar.filterAll')}</option>
              <option value="notified">{t('quittances.toolbar.filterNotified')}</option>
              <option value="notNotified">{t('quittances.toolbar.filterNotNotified')}</option>
              <option value="settled">{t('quittances.toolbar.filterSettled')}</option>
            </select>
            <div className="relative">
              <Icon
                name="Search"
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                placeholder={t('quittances.toolbar.searchPlaceholder')}
                className="w-64 h-8 pl-8 pr-3 text-xs rounded-md border border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
              />
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            {/* Phase 2 : exports et creation arrivent en phase 3 */}
            <button
              disabled
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg shadow-sm opacity-40 cursor-not-allowed"
            >
              <Icon name="Download" size={13} className="text-gray-500" />
              CSV
            </button>
            <button
              disabled
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg shadow-sm opacity-40 cursor-not-allowed"
            >
              <Icon name="FileText" size={13} className="text-red-500" />
              PDF
            </button>
            <button
              onClick={openCreate}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <Icon name="Plus" size={13} />
              {t('quittances.toolbar.newDenonciation')}
            </button>
          </div>
        </div>
      </div>

      {/* ── Onglet QUITTANCES ── */}
      {activeTab === 'quittances' && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden min-h-0 flex flex-col">
          {rows?.length === 0 ? (
            <div className="p-12 text-center">
              <Icon name="Users" size={40} className="mx-auto mb-3 text-gray-300" />
              <p className="text-sm text-gray-500">{t('quittances.empty.noSubcontracts')}</p>
              <p className="text-xs text-gray-400 mt-1">{t('quittances.empty.noSubcontractsHint')}</p>
            </div>
          ) : (
            <div className="min-h-0 overflow-y-auto overflow-x-auto overscroll-contain">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr className="border-b border-gray-200">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-700 align-top">
                      {t('quittances.table.subcontractor')}
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-700 align-top whitespace-nowrap">
                      {t('quittances.table.dateDenonciation')}
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-700 whitespace-nowrap align-top">
                      <span className="block">{t('quittances.table.revised')}</span>
                      <span className="block text-[10px] font-normal text-gray-400">
                        {t('quittances.table.taxIncl')}
                      </span>
                    </th>
                    {dpColumns?.map((dp, dpIndex) => (
                      <th
                        key={dp?.id}
                        className="text-right px-4 py-3 text-xs font-semibold text-gray-700 whitespace-nowrap align-top"
                      >
                        <span className="block">
                          {dp?.isNoDp
                            ? t('quittances.table.noDp')
                            : t('quittances.table.dp', { number: dp?.number })}
                        </span>
                        {/* Le cumul ne commence qu'a la 2e colonne ; "Sans DP" reste isolee. */}
                        {!dp?.isNoDp && dpIndex > 0 && (
                          <span className="block text-[10px] font-normal text-gray-400">
                            {t('quittances.table.cumulative')}
                          </span>
                        )}
                      </th>
                    ))}
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-700 whitespace-nowrap align-top">
                      <span className="block">{t('quittances.table.balance')}</span>
                      <span className="block text-[10px] font-normal text-gray-400">
                        {t('quittances.table.remaining')}
                      </span>
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-700 align-top">
                      {t('quittances.table.status')}
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-700 align-top">
                      {t('quittances.emit.column')}
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-700 align-top w-[90px]">
                      {t('quittances.toolbar.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {visibleRows?.map((row) => {
                    const rowQuittances = quittancesBySub?.[row?.id] || [];
                    const hasFinale = rowQuittances?.some((q) => q?.type === 'finale');
                    const status = rowStatus(row, hasFinale);
                    // Plus de `|| hasFinale` : c'est lui qui verdissait une
                    // balance non nulle des qu'une finale existait.
                    const settled = isSettled(row?.balance);
                    return (
                      <tr key={row?.id} className="hover:bg-gray-50/60 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            {/* Marqueur, pas pave : le detail vient au survol,
                                le panneau complet au clic. La ligne garde sa
                                hauteur. Il disparait des que tous les
                                fournisseurs denonces sont quittances. */}
                            {row?.payment_blocked && (
                              <button
                                type="button"
                                onClick={() => setExposureFor(row)}
                                title={supplierTooltip(row)}
                                className="shrink-0 text-red-600 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-primary/20 rounded"
                              >
                                <Icon name="AlertTriangle" size={12} />
                              </button>
                            )}
                            <p className="text-xs font-medium text-gray-900">{row?.name}</p>
                          </div>
                          {row?.trade && <p className="text-[10px] text-gray-400">{row?.trade}</p>}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {row?.denonciation ? (
                            <span className="text-xs text-gray-600">
                              {fmtDate(row?.denonciation?.date_denonciation, language)}
                              <LetterIcon
                                count={row?.denonciation?.documents?.length || 0}
                                title={t('quittances.toolbar.docsLabel')}
                              />
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400 italic">
                              {t('quittances.table.notNotified')}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {/* Trois etats d'une meme famille :
                              regie / sans montant / TTC + HT.
                              Un bon de commande sans valeur de contrat n'a pas
                              de total revise : afficher "0,00 $" affirmerait
                              un fait faux, et deux fois plutot qu'une. */}
                          {row?.isLaborMaterial ? (
                            <span className="text-[10px] text-gray-400 italic">
                              {t('quittances.table.laborMaterial')}
                            </span>
                          ) : row?.contractValue == null ? (
                            <span className="text-[10px] text-gray-400 italic">
                              {t('quittances.toolbar.noAmount')}
                            </span>
                          ) : (
                            <>
                              <span className="block text-xs font-bold text-blue-700">
                                {fmt(row?.revisedContract)}
                              </span>
                              {/* Garde != null avant tout fmt : formatCurrency
                                  rendrait "0,00 $" sur un null. */}
                              {row?.revisedContractHt != null && (
                                <span className="block text-[10px] font-normal text-gray-400">
                                  {fmt(row?.revisedContractHt)} {t('quittances.table.beforeTax')}
                                </span>
                              )}
                            </>
                          )}
                        </td>
                        {dpColumns?.map((dp) => {
                          /* Le montant vient de l'argent REELLEMENT VERSE.
                             Une quittance s'appuie sur un paiement, pas sur
                             une approbation. amount_paid est deja net de
                             retenue : on ne la rededuit pas.

                             Trois etats, jamais un quatrieme :
                               quittance existante                -> vert, signee
                               quittance < paye cumule            -> ambre, cumul depasse
                               paye nouveau sans quittance        -> ambre, a quittancer
                               approuve nouveau, rien de paye     -> gris italique, a prevoir
                               ni approuve ni paye nouveau        -> tiret

                             Une DP visible mais vide EST une information :
                             les colonnes restent construites sur le facture
                             approuve, donc une DP ou rien n'a ete verse
                             s'affiche et montre son tiret. */
                          // Regle R1 : l'etat vient de ce que CETTE DP apporte ; le montant
                          // affiche reste un cumul (paye, ou approuve pour "a prevoir").
                          const cellKey = `${row?.id}::${dp?.id}`;
                          const dpCell = row?.dpCells?.[dp?.id];
                          // Une finale du contrat couvre chaque DP qui a du paye ; son montant
                          // (tout le paye a la signature) sert de reference pour « cumul superieur ».
                          const finale = finaleBySub?.get(row?.id);
                          const coveredByFinale = finale != null && (dpCell?.paidAdded || 0) > 0;
                          const cell = dpCellState(
                            dpCell,
                            dpCoveredByQuittance?.has(cellKey) || coveredByFinale,
                            Math.max(releasedBySubDp?.get(cellKey) || 0, coveredByFinale ? finale : 0)
                          );
                          return (
                            <td key={dp?.id} className="px-4 py-3 text-right whitespace-nowrap">
                              {cell?.kind !== 'empty' ? (
                                <>
                                  {cell?.kind === 'released' ? (
                                    <DpCellDetails
                                      className="inline-flex items-center gap-1 text-xs font-bold text-green-600"
                                      label={t('quittances.table.details.paidInvoices')}
                                      details={dpCell?.details?.paid}
                                      prior={dpCell?.priorPaid}
                                      priorRange={dpCell?.priorRange}
                                      total={cell?.amount}
                                    >
                                      <Icon name="Check" size={11} className="shrink-0" />
                                      {fmt(cell?.amount)}
                                    </DpCellDetails>
                                  ) : cell?.kind === 'planned' ? (
                                    <DpCellDetails
                                      className="text-xs italic text-gray-400"
                                      label={t('quittances.table.details.approvedInvoices')}
                                      details={dpCell?.details?.approved}
                                      prior={dpCell?.priorApproved}
                                      priorRange={dpCell?.priorRange}
                                      total={cell?.amount}
                                    >
                                      {fmt(cell?.amount)}
                                    </DpCellDetails>
                                  ) : (
                                    <DpCellDetails
                                      className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700"
                                      label={t('quittances.table.details.paidInvoices')}
                                      details={dpCell?.details?.paid}
                                      prior={dpCell?.priorPaid}
                                      priorRange={dpCell?.priorRange}
                                      total={cell?.amount}
                                    >
                                      <Icon name="AlertTriangle" size={11} className="shrink-0" />
                                      {fmt(cell?.amount)}
                                    </DpCellDetails>
                                  )}
                                  {/* Les mots seuls : ces montants n'ont pas de
                                      pendant HT natif, on n'en calcule pas un.
                                      Meme clef que l'en-tete "Montant revise". */}
                                  <span className="block text-[10px] font-normal text-gray-400">
                                    {t('quittances.table.taxIncl')}
                                  </span>
                                </>
                              ) : (
                                <span className="text-xs text-gray-300">—</span>
                              )}
                            </td>
                          );
                        })}
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {row?.balance === null ? (
                            <span className="text-xs text-gray-300">—</span>
                          ) : (
                            <>
                              <span
                                className={`block text-xs font-bold ${
                                  settled ? 'text-green-600' : 'text-gray-900'
                                }`}
                              >
                                {fmt(row?.balance)}
                              </span>
                              {/* HT natif : revisedHt - approvedToDateHt.
                                  null en regie, rien sous un zero. */}
                              {row?.balanceHt != null && row?.balanceHt !== 0 && (
                                <span className="block text-[10px] font-normal text-gray-400">
                                  {fmt(row?.balanceHt)} {t('quittances.table.beforeTax')}
                                </span>
                              )}
                            </>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={STATUS_VARIANT?.[status]}>
                            {t(`quittances.status.${status}`)}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          {/* flex-nowrap : les pastilles ne s'empilent jamais.
                              Elles portent le NUMERO, pas le montant — celui-ci
                              est deja dans la colonne de sa DP. */}
                          {rowQuittances?.length === 0 ? (
                            <span className="text-xs text-gray-300">—</span>
                          ) : (
                            <div className="flex flex-nowrap items-center gap-1.5">
                              {rowQuittances?.map((q) => (
                                <QuittanceChip
                                  key={q?.id}
                                  quittance={q}
                                  onOpen={() => setSignedFor({ quittance: q, row })}
                                />
                              ))}
                            </div>
                          )}
                        </td>
                        {/* Actions : un seul bouton, en derniere colonne. */}
                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            onClick={() => openEmit(row)}
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium text-gray-600 bg-white border border-gray-200 rounded-full hover:border-primary hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20"
                          >
                            <Icon name="Plus" size={10} />
                            {t('quittances.emit.action')}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Onglet DENONCIATIONS ── */}
      {activeTab === 'denonciations' && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden min-h-0 flex flex-col">
          {denonciations?.length === 0 ? (
            <div className="p-12 text-center">
              <Icon name="FileCheck" size={40} className="mx-auto mb-3 text-gray-300" />
              <p className="text-sm text-gray-500">{t('quittances.denonciations.empty')}</p>
              <p className="text-xs text-gray-400 mt-1">{t('quittances.denonciations.emptyHint')}</p>
            </div>
          ) : (
            <div className="min-h-0 overflow-y-auto overflow-x-auto overscroll-contain">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr className="border-b border-gray-200">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-700">
                      {t('quittances.denonciations.intervenant')}
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-700">
                      {t('quittances.denonciations.specialite')}
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-700">
                      {t('quittances.denonciations.lieA')}
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-700 whitespace-nowrap">
                      {t('quittances.denonciations.date')}
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-700 whitespace-nowrap">
                      {t('quittances.denonciations.montant')}
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-700">
                      {t('quittances.denonciations.letter')}
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-700 w-[90px]">
                      {t('quittances.toolbar.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {denonciations?.map((d) => (
                    <tr key={d?.id} className="hover:bg-gray-50/60 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-gray-900">
                            {d?.intervenant_name}
                          </span>
                          <Badge variant={d?.is_principal ? 'blue' : 'grey'} dot={false}>
                            {d?.is_principal
                              ? t('quittances.denonciations.principal')
                              : t('quittances.denonciations.secondaire')}
                          </Badge>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600">{d?.specialite || '—'}</td>
                      <td className="px-4 py-3 text-xs text-gray-600">{d?.lie_a || '—'}</td>
                      <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                        {fmtDate(d?.date_denonciation, language)}
                      </td>
                      <td className="px-4 py-3 text-right text-xs font-semibold text-gray-900 whitespace-nowrap">
                        {/* La garde != null precede TOUT appel a fmt :
                            formatCurrency(null) rendrait "0,00 $", indistinct
                            d'un vrai zero. */}
                        {d?.montant_denonce != null ? (
                          <>
                            {fmt(d?.montant_denonce)}
                            {d?.type_denonciation === 'ouverte' && (
                              <span className="ml-1 text-[10px] font-normal text-gray-400">
                                {t('quittances.denonciations.estime')}
                              </span>
                            )}
                          </>
                        ) : d?.type_denonciation === 'ouverte' ? (
                          /* Etat voulu, pas une donnee manquante. */
                          <Badge variant="grey" dot={false}>
                            {t('quittances.toolbar.typeOuverte')}
                          </Badge>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <DocumentCountButton
                          count={d?.documents?.length || 0}
                          onClick={() => setDocsFor(d)}
                          title={t('quittances.toolbar.docsLabel')}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => openEdit(d)}
                            title={t('quittances.toolbar.edit')}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20"
                          >
                            <Icon name="Pencil" size={14} />
                          </button>
                          <button
                            onClick={() => askDeleteDenonciation(d)}
                            title={t('quittances.toolbar.delete')}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20"
                          >
                            <Icon name="Trash2" size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Modale creation / edition ── */}
      {showForm && form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={closeForm} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 flex flex-col max-h-[90vh]">
            {/* En-tete */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-900">
                {editing
                  ? t('quittances.toolbar.editDenonciation')
                  : t('quittances.toolbar.newDenonciation')}
              </h2>
              <button
                onClick={closeForm}
                className="p-1 rounded text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <Icon name="X" size={16} />
              </button>
            </div>

            {/* Corps */}
            <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
              {formError && (
                <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {formError}
                </div>
              )}

              {/* 1. QUI DENONCE — premier choix, car il change le sens de tout
                  ce qui suit : le libelle du selecteur de contrat, le
                  prefill de l'intervenant, et ce que "Contrat avec" resout. */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  {t('quittances.toolbar.typeLabel')} <span className="text-red-500">*</span>
                </label>
                <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => onToggleType(true)}
                    className={`flex-1 px-3 py-2 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                      form?.isPrincipal
                        ? 'bg-primary text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {t('quittances.toolbar.typePrincipal')}
                  </button>
                  <button
                    type="button"
                    onClick={() => onToggleType(false)}
                    className={`flex-1 px-3 py-2 text-xs font-medium border-l border-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                      !form?.isPrincipal
                        ? 'bg-primary text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {t('quittances.toolbar.typeSecondaire')}
                  </button>
                </div>
              </div>

              {/* UN SEUL selecteur de contrat, pour les deux types.
                  PRINCIPALE : le contrat du sous-traitant qui se denonce.
                  SECONDAIRE : le contrat du sous-traitant que ce fournisseur
                  approvisionne — celui qu'il ne faudra pas payer.
                  Obligatoire des deux cotes, donc pas de `allowNone`. */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {form?.isPrincipal
                    ? t('quittances.toolbar.subcontractLink')
                    : t('quittances.toolbar.contractTargeted')}{' '}
                  <span className="text-red-500">*</span>
                </label>
                <SearchableSelect
                  options={subcontractOptions}
                  value={form?.subcontractId}
                  onChange={(v) => onPickSubcontract(v || '')}
                  placeholder={
                    form?.isPrincipal
                      ? t('quittances.toolbar.subcontractLinkPlaceholder')
                      : t('quittances.toolbar.contractTargetedPlaceholder')
                  }
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {form?.isPrincipal
                      ? t('quittances.toolbar.intervenantName')
                      : t('quittances.toolbar.supplierName')}{' '}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form?.intervenantName}
                    onChange={(e) => setField('intervenantName', e?.target?.value)}
                    placeholder={
                      form?.isPrincipal
                        ? t('quittances.toolbar.intervenantNamePlaceholder')
                        : t('quittances.toolbar.supplierNamePlaceholder')
                    }
                    className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {t('quittances.denonciations.specialite')}
                  </label>
                  <input
                    type="text"
                    value={form?.specialite}
                    onChange={(e) => setField('specialite', e?.target?.value)}
                    placeholder={t('quittances.toolbar.specialitePlaceholder')}
                    className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {t('quittances.denonciations.lieA')}
                </label>
                {/* Toujours en lecture seule : lie_a n'est plus saisi, il
                    reflete le contrat choisi ci-dessus. */}
                <div className="relative">
                  <input
                    type="text"
                    value={derivedLieA}
                    readOnly
                    placeholder={t('quittances.toolbar.lieAPlaceholder')}
                    className="w-full text-xs border border-gray-200 rounded-lg pl-3 pr-8 py-2 bg-gray-50 text-gray-500 cursor-default focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                  <Icon
                    name="Lock"
                    size={12}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                </div>
                <p className="text-[10px] text-gray-400 mt-1">
                  {form?.isPrincipal
                    ? t('quittances.toolbar.lieALockedHint')
                    : t('quittances.toolbar.lieAFromContractHint')}
                </p>
              </div>

              {/* Contexte du contrat choisi : ce qui DERIVE la nature de la
                  denonciation. Affiche pour que la regle ne soit pas opaque.
                  contractValue est la valeur BRUTE, NULL preserve : la garde
                  != null precede tout appel a fmt, sans quoi formatCurrency
                  rendrait "0,00 $" pour un contrat sans montant. */}
              {selectedSubcontract && (
                <div className="flex items-center gap-2 rounded-lg bg-gray-50 border border-gray-200 px-3 py-2">
                  <Icon name="FileText" size={12} className="text-gray-400 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[10px] text-gray-400">
                      {t('quittances.toolbar.contractContextLabel')}
                    </p>
                    <p className="text-xs text-gray-700">
                      {t(
                        PRICING_LABEL_KEY?.[selectedSubcontract?.pricingModel] ||
                          PRICING_LABEL_KEY?.fixed
                      )}
                      {' · '}
                      {selectedSubcontract?.contractValue != null
                        ? fmt(selectedSubcontract?.contractValue)
                        : t('quittances.toolbar.noAmount')}
                    </p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {t('quittances.toolbar.dateLabel')}
                  </label>
                  <input
                    type="date"
                    value={form?.dateDenonciation}
                    onChange={(e) => setField('dateDenonciation', e?.target?.value)}
                    className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {t('quittances.toolbar.montantLabel')}{' '}
                    {isOuverte ? (
                      <span className="text-gray-400 font-normal">
                        {t('quittances.toolbar.montantOptional')}
                      </span>
                    ) : (
                      <span className="text-red-500">*</span>
                    )}
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form?.montantDenonce}
                    onChange={(e) => setField('montantDenonce', e?.target?.value)}
                    placeholder="0.00"
                    className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                  {/* Dire POURQUOI le montant est facultatif : sans cela la
                      regle derivee parait arbitraire. */}
                  {isOuverte && (
                    <p className="text-[10px] text-gray-400 mt-1">
                      {t('quittances.toolbar.ouverteHint')}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {t('quittances.toolbar.notesLabel')}
                </label>
                <textarea
                  rows={3}
                  value={form?.notes}
                  onChange={(e) => setField('notes', e?.target?.value)}
                  placeholder={t('quittances.toolbar.notesPlaceholder')}
                  className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {t('quittances.toolbar.docsLabel')}
                </label>
                <DocumentAttach
                  // A la creation, la denonciation n'a pas encore d'id : les
                  // fichiers sont mis EN ATTENTE et televerses apres l'INSERT.
                  // Les passer en `documents` fait profiter la mise en attente
                  // de la detection de doublons du composant partage.
                  documents={editing ? (editing?.documents || []) : stagedDocs}
                  onOpen={editing ? openDoc : undefined}
                  onAdd={editing ? (file) => handleAttach(editing, file) : stageFile}
                  onRemove={editing ? setPendingDocDelete : unstageFile}
                  onComplete={editing ? handleAttachComplete : undefined}
                  hint={t('quittances.toolbar.lettreHint')}
                />
                {!editing && stagedFiles?.length > 0 && (
                  <p className="text-[10px] text-gray-400 mt-1">
                    {t('quittances.toolbar.docsPendingHint', { count: stagedFiles?.length })}
                  </p>
                )}
              </div>
            </div>

            {/* Pied */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button
                onClick={closeForm}
                disabled={saving}
                className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                {saving && <Icon name="Loader2" size={13} className="animate-spin" />}
                {saving ? t('common.saving') : t('quittances.toolbar.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirmation de suppression ── */}
      <ConfirmDialog
        isOpen={!!pendingDelete}
        title={
          denonIsBlocked
            ? t('quittances.toolbar.deleteBlockedTitle')
            : t('quittances.toolbar.deleteTitle')
        }
        message={
          denonIsBlocked
            ? t('quittances.toolbar.deleteBlockedMessage')
            : denonBlockers
              ? t('quittances.toolbar.deleteMessage')
              : t('quittances.toolbar.deleteChecking')
        }
        confirmLabel={
          denonIsBlocked ? t('quittances.toolbar.deleteBlockedClose') : t('common.delete')
        }
        cancelLabel={t('common.cancel')}
        confirmVariant={denonIsBlocked ? 'primary' : 'danger'}
        confirmOnly={denonIsBlocked}
        icon={denonIsBlocked ? 'AlertTriangle' : null}
        loading={deleting}
        onConfirm={
          denonIsBlocked
            ? () => { setPendingDelete(null); setDenonBlockers(null); }
            : confirmDelete
        }
        onCancel={() => { setPendingDelete(null); setDenonBlockers(null); }}
      >
        <p className="text-sm text-gray-500 truncate mt-2">
          {pendingDelete?.intervenant_name}
        </p>
        {denonIsBlocked && (
          <ul className="mt-3 space-y-1">
            <li className="flex items-center gap-2 text-sm text-gray-700">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />
              {t('quittances.toolbar.blockedQuittances', { count: denonBlockers?.quittances })}
            </li>
          </ul>
        )}
        {/* Informatif : les documents sont en CASCADE, ils ne bloquent rien.
            Mais partir avec trois pieces jointes sans le savoir n'est pas la
            meme decision que supprimer une fiche vide. */}
        {!denonIsBlocked && (denonBlockers?.documents || 0) > 0 && (
          <p className="mt-3 text-xs text-amber-600">
            {t('quittances.toolbar.deleteAlsoRemoves', { count: denonBlockers?.documents })}
          </p>
        )}
      </ConfirmDialog>

      {/* ── Documents d'une denonciation, depuis la colonne ── */}
      {docsFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setDocsFor(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden flex flex-col max-h-[85vh]">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2 min-w-0">
                <Icon name="FolderOpen" size={16} className="text-primary shrink-0" />
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-gray-900">
                    {t('quittances.toolbar.docsLabel')}
                  </h3>
                  <p className="text-xs text-gray-500 truncate">{docsFor?.intervenant_name}</p>
                </div>
              </div>
              <button
                onClick={() => setDocsFor(null)}
                className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
            <div className="px-5 py-4 overflow-y-auto">
              <DocumentAttach
                documents={docsFor?.documents || []}
                onOpen={openDoc}
                onAdd={(file) => handleAttach(docsFor, file)}
                onRemove={setPendingDocDelete}
                onComplete={handleAttachComplete}
                hint={t('quittances.toolbar.lettreHint')}
              />
            </div>
              <div className="flex items-center justify-end px-5 py-4 border-t border-gray-100">
                <button
                  onClick={() => setDocsFor(null)}
                  className="px-4 py-2 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  {t('documents.done')}
                </button>
              </div>
          </div>
        </div>
      )}

      {/* ── Modale d'emission d'une quittance ── */}
      {emitFor && qForm && (() => {
        // Deux camps distincts : les quittances du sous-traitant ne bloquent
        // pas celles du fournisseur, et reciproquement. Cote fournisseur,
        // seule l'unicite de la finale s'applique.
        const duplicate = isSupplierIssuer
          ? findSupplierDuplicate(emitFor, qForm?.issuerDenonciationId, qForm?.type)
          : findDuplicate(emitFor, qForm?.paymentRequestId, qForm?.type);
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={closeEmit} />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 flex flex-col max-h-[90vh]">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <div className="min-w-0">
                  <h2 className="text-sm font-semibold text-gray-900">
                    {t('quittances.emit.title')}
                  </h2>
                  <p className="text-xs text-gray-500 truncate">
                    {emitFor?.name}
                    {emitFor?.costCode ? ` · ${emitFor?.costCode}` : ''}
                  </p>
                </div>
                <button
                  onClick={closeEmit}
                  className="p-1 rounded text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <Icon name="X" size={16} />
                </button>
              </div>

              <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
                {/* Blocage anti-doublon : on propose d'ouvrir l'existante. */}
                {duplicate && (
                  <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    <p>
                      {duplicate?.reason === 'finaleSupplier'
                        ? t('quittances.emit.dupFinaleSupplier')
                        : duplicate?.reason === 'finale'
                          ? t('quittances.emit.dupFinale')
                          : t('quittances.emit.dupDp')}
                    </p>
                    <button
                      type="button"
                      onClick={() => openQuittanceDoc(duplicate?.quittance, emitFor)}
                      className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-red-700 underline hover:text-red-800 focus:outline-none focus:ring-2 focus:ring-primary/20 rounded"
                    >
                      <Icon name="FileText" size={11} />
                      {t('quittances.emit.openExisting')}
                    </button>
                  </div>
                )}

                {qError && !duplicate && (
                  <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    {qError}
                  </div>
                )}

                {/* Affiche UNIQUEMENT si ce contrat porte au moins un
                    fournisseur denonce. Sinon la modale est celle d'avant. */}
                {emitFor?.supplier_details?.length > 0 && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      {t('quittances.emit.issuerLabel')} <span className="text-red-500">*</span>
                    </label>
                    <div className="space-y-1.5">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="quittanceIssuer"
                          checked={!isSupplierIssuer}
                          onChange={() => {
                            setQForm((prev) => ({ ...prev, issuerDenonciationId: null }));
                            setQError('');
                          }}
                          className="accent-[#FF6B2C]"
                        />
                        <span className="text-xs text-gray-700">
                          {t('quittances.emit.issuerSubcontractor', { name: emitFor?.name })}
                        </span>
                      </label>
                      {emitFor?.supplier_details?.map((sd) => (
                        <label
                          key={sd?.denonciation_id}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <input
                            type="radio"
                            name="quittanceIssuer"
                            checked={qForm?.issuerDenonciationId === sd?.denonciation_id}
                            onChange={() => {
                              setQForm((prev) => ({
                                ...prev,
                                issuerDenonciationId: sd?.denonciation_id,
                              }));
                              setQError('');
                            }}
                            className="accent-[#FF6B2C]"
                          />
                          <span className="text-xs text-gray-700">
                            {t('quittances.emit.issuerSupplier', { name: sd?.intervenant_name })}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Un fournisseur n'a pas de demande de paiement. */}
                {!isSupplierIssuer && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {t('quittances.emit.dpLabel')}
                  </label>
                  {/* Meme composant que la modale de denonciation. Pas de
                      allowNone : le champ est requis, et le composant partage
                      y injecterait un "None" non traduit. */}
                  <SearchableSelect
                    options={emittableDpOptions?.map((o) => ({
                      value: o?.value,
                      label: o?.isNoDp
                        ? t('quittances.table.noDp')
                        // Selecteur de DEMANDES DE PAIEMENT : chipNumbered dit
                        // "Quittance N", juste sur une pastille, faux ici.
                        : t('quittances.emit.dpOption', { number: o?.number }),
                    }))}
                    value={qForm?.paymentRequestId}
                    onChange={(v) => onPickDp(v || '')}
                    placeholder={t('quittances.emit.dpLabel')}
                  />
                  {qForm?.type === 'finale' && (
                    <p className="text-[10px] text-gray-400 mt-1">
                      {t('quittances.emit.finaleLocksDp')}
                    </p>
                  )}
                </div>
                )}

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    {t('quittances.emit.typeLabel')}
                  </label>
                  <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                    {['partielle', 'finale']?.map((tp, i) => (
                      <button
                        key={tp}
                        type="button"
                        onClick={() => onPickQuittanceType(tp)}
                        className={`flex-1 px-3 py-2 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                          i > 0 ? 'border-l border-gray-200' : ''
                        } ${
                          qForm?.type === tp
                            ? 'bg-primary text-white'
                            : 'bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        {t(`quittances.emit.type.${tp}`)}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {t('quittances.emit.amountLabel')}
                  </label>
                  {isSupplierIssuer ? (
                    <>
                      {/* Rappel en lecture seule : ou en est ce fournisseur.
                          On informe, on n'impose rien. Garde != null avant fmt. */}
                      <p className="text-[10px] text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 mb-1">
                        {t('quittances.emit.supplierRecap', {
                          denounced:
                            issuerDetail?.montant_denonce != null
                              ? fmt(issuerDetail?.montant_denonce)
                              : t('quittances.toolbar.noAmount'),
                          released: fmt(issuerDetail?.quittance_total || 0),
                          remaining:
                            issuerDetail?.montant_denonce != null
                              ? fmt(Math.max(0, issuerDetail?.montant_denonce - (issuerDetail?.quittance_total || 0)))
                              : t('quittances.toolbar.noAmount'),
                        })}
                      </p>
                      {/* Saisie libre : nous n'avons aucune facture de ce
                          fournisseur, rien n'est calculable. */}
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={qForm?.supplierMontant}
                        onChange={(e) =>
                          setQForm((prev) => ({ ...prev, supplierMontant: e?.target?.value }))
                        }
                        placeholder="0.00"
                        className="w-full text-right text-xs font-semibold text-gray-900 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      />
                    </>
                  ) : (
                  <>
                  {/* CALCULE, pas saisi. Meme traitement visuel que la colonne
                      "Montant revise" pour que le chiffre se reconnaisse d'un
                      ecran a l'autre. */}
                  <div className="w-full text-right rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                    {montantIsZero ? (
                      <span className="text-[10px] text-gray-400 italic">
                        {t('quittances.toolbar.noAmount')}
                      </span>
                    ) : (
                      <span className="text-xs font-semibold text-gray-900">
                        {fmt(computedMontant)}
                      </span>
                    )}
                  </div>
                  {/* D'ou sort le chiffre : sans cela l'utilisateur s'en mefie. */}
                  <p className={`text-[10px] mt-1 ${emitBlocked ? 'text-red-600' : 'text-gray-400'}`}>
                    {montantIsZero
                      ? t('quittances.emit.amountRequired')
                      : finaleBlocked
                        ? t('quittances.emit.finaleNeedsBalance', { amount: fmt(emitFor?.balance) })
                        : partielleBlocked
                          ? t('quittances.emit.dpNoNewInvoice')
                          : qForm?.type === 'finale'
                            ? t('quittances.emit.amountFromAll')
                            : t('quittances.emit.amountFromDp', {
                                number: dpOptions?.find((o) => o?.value === qForm?.paymentRequestId)?.number
                                  ?? t('quittances.table.noDp'),
                              })}
                  </p>
                  {/* Regie ou bon de commande : aucun montant contractuel de
                      reference, le solde n'est pas verifiable. On le dit. */}
                  {qForm?.type === 'finale' && balanceUnverifiable && (
                    <p className="text-[10px] text-amber-600 mt-1">
                      {t('quittances.emit.balanceUnverifiable')}
                    </p>
                  )}
                  {/* R4 : le rattachement est automatique, on le dit. */}
                  {emitFor?.denonciationPrincipale && (
                    <p className="text-[10px] text-gray-400 mt-1">
                      {emitFor?.denonciationPrincipale?.date_denonciation
                        ? t('quittances.emit.linkedToNotice', {
                            date: fmtDate(emitFor?.denonciationPrincipale?.date_denonciation, language),
                          })
                        : t('quittances.emit.linkedToNoticeNoDate')}
                    </p>
                  )}
                  </>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {t('quittances.emit.dateLabel')}
                  </label>
                  <input
                    type="date"
                    value={qForm?.dateQuittance}
                    onChange={(e) => setQForm((prev) => ({ ...prev, dateQuittance: e?.target?.value }))}
                    className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {t('quittances.toolbar.notesLabel')}
                  </label>
                  <textarea
                    rows={2}
                    value={qForm?.notes}
                    onChange={(e) => setQForm((prev) => ({ ...prev, notes: e?.target?.value }))}
                    placeholder={t('quittances.toolbar.notesPlaceholder')}
                    className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {t('quittances.signed.title')}
                  </label>
                  {/* Meme mise en attente que la modale fournisseur : la
                      quittance n'a pas encore d'id, le fichier part apres
                      l'insert. Un seul document : c'est la piece qui fait foi. */}
                  <DocumentAttach
                    documents={
                      emitStagedFile
                        ? [{
                            id: 'emit-staged',
                            file_name: emitStagedFile?.name,
                            mime_type: emitStagedFile?.type,
                            size_bytes: emitStagedFile?.size,
                          }]
                        : []
                    }
                    onAdd={(file) => {
                      const problem = validateLettreFile(file);
                      if (problem) {
                        showToast(
                          'error',
                          problem === 'FILE_TOO_LARGE'
                            ? t('quittances.toolbar.lettreTooLarge')
                            : t('quittances.toolbar.lettreInvalidType')
                        );
                        return;
                      }
                      setEmitStagedFile(file);
                    }}
                    onRemove={() => setEmitStagedFile(null)}
                    hint={t('quittances.toolbar.lettreHint')}
                    multiple={false}
                  />
                </div>

                <p className="text-[10px] text-gray-400 leading-relaxed">
                  {t('quittances.emit.legalNotice')}
                </p>
              </div>

              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
                <button
                  onClick={closeEmit}
                  disabled={emitting}
                  className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleEmit}
                  disabled={emitting || !!duplicate || emitBlocked}
                  className="px-4 py-2 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  {emitting && <Icon name="Loader2" size={13} className="animate-spin" />}
                  {emitting ? t('common.saving') : t('quittances.emit.confirm')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Preuve : qui bloque le paiement de ce contrat ── */}
      {/* Une modale plutot qu'un popover : le tableau est en overflow-x-auto,
          un panneau ancre dans une cellule y serait rogne. */}
      {exposureFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setExposureFor(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden flex flex-col max-h-[85vh]">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2 min-w-0">
                <Icon
                  name={exposureFor?.suppliers_uncovered_count > 0 ? 'ShieldAlert' : 'ShieldQuestion'}
                  size={16}
                  className={exposureFor?.suppliers_uncovered_count > 0 ? 'text-red-500 shrink-0' : 'text-amber-500 shrink-0'}
                />
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-gray-900">
                    {t('quittances.exposure.title')}
                  </h3>
                  <p className="text-xs text-gray-500 truncate">{exposureFor?.name}</p>
                </div>
              </div>
              <button
                onClick={() => setExposureFor(null)}
                className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <Icon name="X" size={16} />
              </button>
            </div>

            <div className="px-5 py-4 overflow-y-auto space-y-2">
              <p className="text-[11px] text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                {t('quittances.exposure.explain')}
              </p>

              {exposureFor?.supplier_details?.map((sd) => (
                <div
                  key={sd?.denonciation_id}
                  className="flex items-start justify-between gap-3 px-3 py-2 rounded-lg border border-gray-100 hover:bg-gray-50/60 transition-colors"
                >
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-900 truncate">
                      {sd?.intervenant_name}
                    </p>
                    <p className="text-[10px] text-gray-500">
                      {/* Un montant inconnu s'affiche comme tel : on ne
                          fabrique pas un chiffre pour remplir la ligne. */}
                      {t('quittances.exposure.denounced')}{' '}
                      {sd?.montant_denonce == null
                        ? t('quittances.exposure.unknownAmount')
                        : fmt(sd?.montant_denonce)}
                      {' · '}
                      {t('quittances.exposure.released')} {fmt(sd?.quittance_total)}
                    </p>
                    {/* SES quittances, une par une. Le cumul seul rendait deux
                        versements identiques indistinguables et ingerables.
                        `label` nomme le FOURNISSEUR dans les fenetres qui
                        suivent, pas le sous-traitant. */}
                    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                      {sd?.quittances?.length > 0 ? (
                        sd?.quittances?.map((q) => (
                          <QuittanceChip
                            key={q?.id}
                            quittance={q}
                            isSupplier
                            onOpen={() =>
                              setSignedFor({
                                quittance: q,
                                row: exposureFor,
                                label: sd?.intervenant_name,
                                isSupplier: true,
                              })
                            }
                          />
                        ))
                      ) : (
                        <span className="text-xs text-gray-300">—</span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <Badge variant={COVERAGE_VARIANT?.[
                      sd?.status === 'covered' ? 'full' : sd?.status === 'partial' ? 'partial' : 'none'
                    ]}>
                      {t(`quittances.exposure.status.${sd?.status}`)}
                    </Badge>
                    {/* Le chemin de preuve : deux clics jusqu'a la piece. */}
                    <button
                      type="button"
                      onClick={() => {
                        setSearchQuery(sd?.intervenant_name || '');
                        setActiveTab('denonciations');
                        setExposureFor(null);
                      }}
                      className="text-[10px] text-primary hover:text-primary-dark hover:underline focus:outline-none focus:ring-2 focus:ring-primary/20 rounded"
                    >
                      {t('quittances.exposure.seeNotice')}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-end px-5 py-4 border-t border-gray-100">
              <button
                onClick={() => setExposureFor(null)}
                className="px-4 py-2 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                {t('documents.done')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Quittance signee ── */}
      {signedFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setSignedFor(null)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden flex flex-col max-h-[85vh]">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2 min-w-0">
                <Icon name="ShieldCheck" size={16} className="text-primary shrink-0" />
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-gray-900">
                    {t('quittances.signed.title')}
                  </h3>
                  <p className="text-xs text-gray-500 truncate">
                    {/* `label` prime quand il existe : une quittance de
                        fournisseur ne doit pas s'annoncer au nom du
                        sous-traitant. */}
                    {signedFor?.label || signedFor?.row?.name} —{' '}
                    {t(`quittances.emit.type.${signedFor?.quittance?.type}`)} ·{' '}
                    {fmt(signedFor?.quittance?.montant)}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSignedFor(null)}
                className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
            <div className="px-5 py-4 overflow-y-auto">
              <DocumentAttach
                documents={signedDocOf(signedFor?.quittance)}
                onOpen={openDoc}
                onAdd={handleAttachSigned}
                onRemove={() => setPendingSignedDelete(signedFor)}
                onComplete={fetchData}
                uploading={uploadingSigned}
                hint={t('quittances.signed.hint')}
                multiple={false}
              />
              <p className="text-[10px] text-gray-400 mt-3 leading-relaxed">
                {t('quittances.signed.replaceHint')}
              </p>
            </div>
            <div className="flex items-center justify-between px-5 py-4 border-t border-gray-100">
              {/* La suppression vit ici, plus au survol d'une pastille : un
                  clic entre un document legal et sa disparition, c'etait trop
                  peu de friction. */}
              <button
                onClick={() => {
                  setPendingQDelete(signedFor);
                  setSignedFor(null);
                }}
                className="px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                {t('quittances.emit.deleteTitle')}
              </button>
              <button
                onClick={() => setSignedFor(null)}
                className="px-4 py-2 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                {t('documents.done')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirmation retrait de la quittance signee ── */}
      <ConfirmDialog
        isOpen={!!pendingSignedDelete}
        title={t('quittances.signed.removeTitle')}
        message={t('quittances.signed.removeMessage')}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        confirmVariant="danger"
        loading={deletingSigned}
        onConfirm={confirmSignedDelete}
        onCancel={() => setPendingSignedDelete(null)}
      >
        <p className="text-sm text-gray-500 truncate mt-2">
          {baseName(pendingSignedDelete?.quittance?.lettre_url)}
        </p>
      </ConfirmDialog>

      {/* ── Confirmation suppression d'une quittance ── */}
      <ConfirmDialog
        isOpen={!!pendingQDelete}
        title={t('quittances.emit.deleteTitle')}
        message={t('quittances.emit.deleteMessage')}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        confirmVariant="danger"
        loading={deletingQ}
        onConfirm={confirmQuittanceDelete}
        onCancel={() => setPendingQDelete(null)}
      >
        {/* Nommer PRECISEMENT la quittance : avec trois pastilles a l'ecran,
            "cette quittance" ne dit pas laquelle part. */}
        <p className="text-sm font-medium text-gray-900 mt-2">
          {pendingQDelete?.quittance
            ? quittanceChipLabel(pendingQDelete?.quittance, pendingQDelete?.isSupplier)
            : ''}
          {/* Le libelle fournisseur porte deja le montant : ne pas le doubler. */}
          {!pendingQDelete?.isSupplier && pendingQDelete?.quittance?.montant != null
            ? ` · ${fmt(pendingQDelete?.quittance?.montant)}`
            : ''}
        </p>
        {/* A QUI elle appartient : avec plusieurs quittances a l'ecran, le
            libelle seul ne suffit pas. */}
        <p className="text-xs text-gray-500 truncate">
          {pendingQDelete?.label || pendingQDelete?.row?.name}
        </p>
      </ConfirmDialog>

      {/* ── Confirmation suppression de document ── */}
      <ConfirmDialog
        isOpen={!!pendingDocDelete}
        title={t('quittances.toolbar.docsRemoveTitle')}
        message={t('quittances.toolbar.docsRemoveMessage')}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        confirmVariant="danger"
        loading={deletingDoc}
        onConfirm={confirmDocDelete}
        onCancel={() => setPendingDocDelete(null)}
      >
        <p className="text-sm text-gray-500 truncate mt-2">
          {pendingDocDelete?.file_name}
        </p>
      </ConfirmDialog>

      {/* ── Toast ── */}
      {toastMsg && (
        <div
          className={`fixed top-4 right-4 z-[100] flex items-center gap-2 px-4 py-3 text-white text-sm font-medium rounded-xl shadow-lg ${
            toastMsg?.type === 'success' ? 'bg-green-600' : 'bg-red-600'
          }`}
        >
          <Icon name={toastMsg?.type === 'success' ? 'CheckCircle2' : 'AlertTriangle'} size={16} />
          {toastMsg?.text}
        </div>
      )}
    </div>
  );
};

export default QuittancesPage;
